import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {I18nFunction} from '../../../src/i18n/i18n';
import {getBaseCrop, getCrops} from '../../../src/selectors/crops';
import {
  isCereals,
  isForage,
  isFruits,
  isLegumes,
  isOilseeds,
  isVegetables,
  isVines,
} from '../../../src/selectors/harvest';
import cmp from '../../../src/util/cmp';
import {useApis} from '../apis/ApisContext';
import {useCropOptions} from './useCropOptions';

export interface CascaderOption {
  value: string;
  label: string;
  children?: CascaderOption[];
}

// Returns [category, subcategory] for a given crop_id following the detailed hierarchy
function getCropCategory(t: I18nFunction, crop_id: string): [string, string] {
  // Cereals
  if (isCereals(crop_id)) {
    if (crop_id.startsWith('barley')) {
      return [t('cereals'), t('barley')];
    } else if (crop_id.startsWith('corn-grain')) {
      return [t('cereals'), t('corn-grain')];
    } else if (crop_id.startsWith('sorghum') && !crop_id.includes('silage')) {
      return [t('cereals'), t('sorghum-grain')];
    } else if (crop_id.startsWith('wheat-hard') || crop_id.includes('durum')) {
      return [t('cereals'), t('wheat-durum')];
    } else if (crop_id.startsWith('wheat')) {
      return [t('cereals'), t('wheat-soft')];
    } else if (crop_id.startsWith('oats')) {
      return [t('cereals'), t('oats')];
    } else if (crop_id.startsWith('triticale')) {
      return [t('cereals'), t('triticale')];
    } else if (crop_id.startsWith('rye')) {
      return [t('cereals'), t('rye')];
    } else if (crop_id.startsWith('rice')) {
      return [t('cereals'), t('rice')];
    }
    return [t('cereals'), t('other-cereals')];
  }

  // Forage
  if (isForage(crop_id)) {
    return [t('forage'), t('grassland-silage')];
  }

  // Fruits - hierarchical structure
  if (
    // Grapes are also considered fruits, but they belong to vines
    !isVines(crop_id) &&
    (isFruits(crop_id) ||
      crop_id.startsWith('prune') ||
      crop_id.startsWith('walnut') ||
      crop_id.startsWith('hazelnut') ||
      crop_id.startsWith('almond') ||
      crop_id.startsWith('chestnut') ||
      crop_id.startsWith('citrus') ||
      crop_id.startsWith('orange') ||
      crop_id.startsWith('citron') ||
      crop_id.startsWith('avocado') ||
      crop_id.startsWith('banana') ||
      crop_id.startsWith('pineapple') ||
      crop_id.startsWith('raspberry') ||
      crop_id.startsWith('blueberry'))
  ) {
    // Pome fruits
    if (crop_id.startsWith('apples') || crop_id.startsWith('pears')) {
      return [t('fruits'), t('pome-fruits')];
    }

    // Stone fruits
    else if (
      crop_id.startsWith('apricot') ||
      crop_id.startsWith('peach') ||
      crop_id.startsWith('cherry') ||
      crop_id.startsWith('cherries') ||
      crop_id.startsWith('prune') ||
      crop_id.startsWith('olives')
    ) {
      return [t('fruits'), t('stone-fruits')];
    }

    // Nuts
    else if (
      crop_id.startsWith('walnut') ||
      crop_id.startsWith('walnuts') ||
      crop_id.startsWith('hazelnut') ||
      crop_id.startsWith('almond') ||
      crop_id.startsWith('chestnut')
    ) {
      return [t('fruits'), t('nuts')];
    }

    // Citrus
    else if (crop_id.startsWith('orange') || crop_id.startsWith('citron')) {
      return [t('fruits'), t('citrus')];
    }

    // Tropical fruits
    else if (
      crop_id.startsWith('avocado') ||
      crop_id.startsWith('banana') ||
      crop_id.startsWith('mango') ||
      crop_id.startsWith('pineapple')
    ) {
      return [t('fruits'), t('tropical-fruits')];
    }

    // Small fruits
    else if (crop_id.startsWith('raspberry') || crop_id.startsWith('blueberry')) {
      return [t('fruits'), t('small-fruits')];
    }

    return [t('fruits'), t('other-fruits')];
  }

  // Industrial Crops
  if (crop_id.startsWith('sugar-beet')) {
    return [t('industrial-crops'), t('sugar-beet')];
  } else if (crop_id.startsWith('sugar-cane')) {
    return [t('industrial-crops'), t('sugar-cane')];
  } else if (crop_id.startsWith('tobacco')) {
    return [t('industrial-crops'), t('tobacco')];
  } else if (crop_id.startsWith('cotton')) {
    return [t('industrial-crops'), t('cotton')];
  } else if (crop_id.startsWith('hemp')) {
    return [t('industrial-crops'), t('hemp')];
  } else if (crop_id.startsWith('linen')) {
    return [t('industrial-crops'), t('linen')];
  } else if (crop_id.startsWith('hop')) {
    return [t('industrial-crops'), t('hop')];
  } else if (crop_id.startsWith('lavender')) {
    return [t('industrial-crops'), t('lavender')];
  }

  // Legumes
  if (isLegumes(crop_id) || crop_id.startsWith('lupins') || crop_id.startsWith('fava')) {
    if (crop_id.startsWith('peas') && !crop_id.includes('dried')) {
      return [t('legumes'), t('pulses')];
    } else if (crop_id.startsWith('fava') || crop_id.includes('fava')) {
      return [t('legumes'), t('fava-beans')];
    } else if (crop_id.startsWith('lupins')) {
      return [t('legumes'), t('lupins')];
    } else if (crop_id.includes('dried') && crop_id.includes('peas')) {
      return [t('legumes'), t('dried-peas')];
    } else if (crop_id.includes('dried') && crop_id.includes('beans')) {
      return [t('legumes'), t('dried-beans')];
    } else if (crop_id.startsWith('lentils')) {
      return [t('legumes'), t('lentils')];
    }
    return [t('legumes'), t('pulses')];
  }

  // Oilseeds
  if (isOilseeds(crop_id)) {
    if (crop_id.startsWith('rapeseed')) {
      return [t('oilseeds'), t('rapeseed')];
    } else if (crop_id.startsWith('sunflower')) {
      return [t('oilseeds'), t('sunflower')];
    } else if (crop_id.startsWith('soybean')) {
      return [t('oilseeds'), t('soybeans')];
    }
  }

  // Tubers
  if (crop_id.startsWith('potatoes')) {
    return [t('tubers'), t('potatoes')];
  }

  // Vegetables - hierarchical structure
  if (
    isVegetables(crop_id) ||
    crop_id.startsWith('artichokes') ||
    crop_id.startsWith('cabbage') ||
    crop_id.startsWith('asparagus') ||
    crop_id.startsWith('celery') ||
    crop_id.startsWith('endives') ||
    crop_id.startsWith('spinach') ||
    crop_id.startsWith('leeks') ||
    crop_id.startsWith('lettuce') ||
    crop_id.startsWith('eggplants') ||
    crop_id.startsWith('strawberries') ||
    crop_id.startsWith('cucurbits') ||
    crop_id.startsWith('cucumber') ||
    crop_id.startsWith('zucchini') ||
    crop_id.startsWith('peppers') ||
    crop_id.startsWith('carrots') ||
    crop_id.startsWith('turnip') ||
    crop_id.startsWith('garlic') ||
    crop_id.startsWith('shallot') ||
    crop_id.startsWith('beetroot') ||
    crop_id.startsWith('celeriac') ||
    crop_id.startsWith('radish') ||
    crop_id.startsWith('salsify')
  ) {
    // Stem and leaf vegetables
    if (
      crop_id.startsWith('artichokes') ||
      crop_id.startsWith('cabbage') ||
      crop_id.startsWith('asparagus') ||
      crop_id.startsWith('celery') ||
      crop_id.startsWith('endives') ||
      crop_id.startsWith('spinach') ||
      crop_id.startsWith('leeks') ||
      crop_id.startsWith('lettuce')
    ) {
      return [t('vegetables'), t('stem-leaf-vegetables')];
    }

    // Fruit vegetables
    else if (
      crop_id.startsWith('tomatoes') ||
      crop_id.startsWith('eggplants') ||
      crop_id.startsWith('strawberries') ||
      crop_id.startsWith('cucurbits') ||
      crop_id.startsWith('cucumber') ||
      crop_id.startsWith('zucchini') ||
      crop_id.startsWith('peppers')
    ) {
      return [t('vegetables'), t('fruit-vegetables')];
    }

    // Root and tuber vegetables
    else if (
      crop_id.startsWith('carrots') ||
      crop_id.startsWith('turnip') ||
      crop_id.startsWith('garlic') ||
      crop_id.startsWith('onion') ||
      crop_id.startsWith('shallot') ||
      crop_id.startsWith('beetroot') ||
      crop_id.startsWith('celeriac') ||
      crop_id.startsWith('radish') ||
      crop_id.startsWith('salsify')
    ) {
      return [t('vegetables'), t('root-tuber-vegetables')];
    }

    // Leguminous vegetables
    else if ((crop_id.startsWith('peas') || crop_id.startsWith('beans')) && !crop_id.includes('dried')) {
      return [t('vegetables'), t('leguminous-vegetables')];
    }

    return [t('vegetables'), t('other-vegetables')];
  }

  // Vines
  if (isVines(crop_id) || crop_id.startsWith('grapes') || crop_id.startsWith('vin-')) {
    if (crop_id.includes('table') || crop_id.includes('de-table')) {
      return [t('vines'), t('grapes-table')];
    }
    return [t('vines'), t('grapes-wine')];
  }

  // Other Crops
  return [t('other-crops'), t('miscellaneous')];
}

export function useCascaderCropOptions(enabled: boolean): {
  cascaderOptions: CascaderOption[];
  isFetching: boolean;
  options: {value: string; label: string}[];
} {
  const {t} = useApis();
  const {options, isFetching} = useCropOptions(enabled, true);
  const crops = useSelector(getCrops);
  // Transform flat crop options into 3-level hierarchical cascader options
  // Level 1: Category (e.g. Cereals, Fruits, Vines, Fruits)
  // Level 2: Subcategory (e.g. Corn, Wheat, Apples)
  // Level 3: Individual crops (e.g. corn spring, wheat hard)
  const cascaderOptions = useMemo<CascaderOption[]>(() => {
    if (!options.length) {
      return [];
    }

    const categoryMap: Record<string, Record<string, {value: string; label: string}[]>> = {};
    for (const option of options) {
      const harvestCrop = getBaseCrop(crops, option.value);
      const cropId = harvestCrop || option.value;
      const [categoryName, subcategoryName] = getCropCategory(t, cropId);

      if (!categoryMap[categoryName]) {
        categoryMap[categoryName] = {};
      }

      if (!categoryMap[categoryName][subcategoryName]) {
        categoryMap[categoryName][subcategoryName] = [];
      }

      categoryMap[categoryName][subcategoryName].push({
        value: option.value,
        label: option.label,
      });
    }

    // Convert to cascader format with 3 levels
    return Object.entries(categoryMap)
      .sort(([a], [b]) => cmp(a, b))
      .map(([categoryName, subcategories]) => {
        return {
          value: categoryName,
          label: categoryName,
          children: Object.entries(subcategories)
            .sort(([a], [b]) => cmp(a, b))
            .map(([subcategoryName, crops]) => ({
              value: subcategoryName,
              label: subcategoryName,
              children: crops.sort((a, b) => cmp(a.label, b.label)),
            })),
        };
      });
  }, [options, crops, t]);

  return {
    cascaderOptions,
    isFetching,
    options,
  };
}
